import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { RevealStagger, StaggerItem, Reveal } from "@/components/Reveal";
import { PROCEDURES } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const SLUGS = {
  "Endoscopic Discectomy": "/endoscopic-discectomy",
  "Endoscopic Decompression": "/endoscopic-decompression",
  "Minimally Invasive Spine Surgery": "/minimally-invasive-spine-surgery",
  "Microscopic Spine Surgery": "/microscopic-spine-surgery",
  "Revision Spine Surgery": "/revision-spine-surgery",
  "Cervical Spine Surgery": "/cervical-spine-surgery",
  "Lumbar Spine Surgery": "/lumbar-spine-surgery",
};

const ANCHORS = {
  "Endoscopic Discectomy": "Endoscopic discectomy in Hyderabad",
  "Endoscopic Decompression": "Endoscopic decompression for stenosis",
  "Minimally Invasive Spine Surgery": "Minimally invasive spine surgery",
  "Microscopic Spine Surgery": "Microscopic spine surgery",
  "Revision Spine Surgery": "Revision spine surgery",
  "Cervical Spine Surgery": "Cervical spine surgery in Hyderabad",
  "Lumbar Spine Surgery": "Lumbar spine surgery",
};

const Procedures = () => {
  const { openBooking } = useBooking();
  return (
    <section id="procedures" data-testid="procedures-section" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">03 — Advanced Procedures</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            Precision procedures, tailored to you
          </h2>
          <p className="mt-5 text-lg text-slate-500">
            A comprehensive range of modern spine surgery techniques — selected only when clinically
            appropriate for your diagnosis.
          </p>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROCEDURES.map((p, i) => {
            const slug = SLUGS[p.title];
            return (
              <StaggerItem key={p.title}>
                <div
                  data-testid={`procedure-card-${i}`}
                  className="group h-full glass-card rounded-2xl p-8 hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300 flex flex-col"
                >
                  <div className="text-brand/25 font-heading font-extrabold text-5xl leading-none mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-heading font-extrabold text-[#0b2c4d] text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-slate-500 text-sm leading-relaxed">{p.desc}</p>

                  <ul className="mt-5 space-y-2">
                    {p.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-brand shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Typical indication</div>
                    <div className="text-sm text-slate-500">{p.indications}</div>
                  </div>

                  {slug ? (
                    <Link
                      to={slug}
                      data-testid={`procedure-learn-${i}`}
                      className="mt-6 inline-flex items-center gap-1 text-sm text-brand font-semibold self-start"
                    >
                      {ANCHORS[p.title] || "Learn More"}
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  ) : (
                    <button
                      onClick={() => openBooking("appointment")}
                      data-testid={`procedure-learn-${i}`}
                      className="mt-6 inline-flex items-center gap-1 text-sm text-brand font-semibold self-start"
                    >
                      Learn More
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  )}
                </div>
              </StaggerItem>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
};

export default Procedures;
